// Single source of truth for Sirius design tokens (color, radius, type).
// Consumed by both frontends via the MUI theme (theme.ts). See the style-guide
// draft for rationale.
//
// Dark mode is a first-class second palette, NOT an inversion: near-black navy
// backgrounds, off-white text, and lightened brand/semantic colors so contrast
// holds. Contrast pairings are enforced in theme.test.ts.

export type ThemeMode = "light" | "dark";

export const lightTokens = {
  // Primary is the classic bright blue ($blue-500 #0091F9) the app used pre-migration,
  // matching the filter chips. White text is ~3.3:1 -- below AA for body text but within
  // the 3:1 UI-component / large-(bold)-text standard that buttons meet (see theme test).
  primary: { main: "#0091F9", light: "#42ABFB", dark: "#0073C7", contrastText: "#FFFFFF" },
  secondary: { main: "#5674B9", light: "#8398CF", dark: "#3C578F", contrastText: "#FFFFFF" },
  success: "#2E7D32",
  warning: "#B26A00", // fills/borders only — AA-Large on white; use for text sparingly
  error: "#C0362C",
  info: "#0277B6",
  text: { primary: "#16212B", secondary: "#5A6874", disabled: "#A2AEB8" },
  background: { default: "#F5F8FA", paper: "#FFFFFF" },
  divider: "#DCE3E9",
  // 11-step slate neutral ramp — replaces the 20+ ad-hoc grays found in the audit.
  neutral: {
    0: "#FFFFFF", 50: "#F5F8FA", 100: "#EAEFF3", 200: "#DCE3E9", 300: "#C4CED6",
    400: "#A2AEB8", 500: "#7A8894", 600: "#5A6874", 700: "#3E4A54", 800: "#28323B", 900: "#16212B",
  },
} as const;

export const darkTokens = {
  // Lightened bright-blue so it holds contrast on the navy dark bg; near-black text on
  // it passes AA (~8:1). Same hue family as light mode.
  primary: { main: "#4DB4FA", light: "#82CBFC", dark: "#0091F9", contrastText: "#0E1620" },
  secondary: { main: "#8AA0D6", light: "#B3C1E6", dark: "#5C74AE", contrastText: "#0E1620" },
  success: "#66BB6A",
  warning: "#E0A030",
  error: "#EF6B62",
  info: "#4FB0E6",
  text: { primary: "#E6EDF2", secondary: "#9FB0BC", disabled: "#5E6E7B" },
  background: { default: "#0E1620", paper: "#16212B" }, // surfaces lighten with elevation
  divider: "#2B3946",
  neutral: {
    0: "#16212B", 50: "#0E1620", 100: "#1E2A35", 200: "#2B3946", 300: "#3A4A57",
    400: "#5E6E7B", 500: "#7C8B97", 600: "#9FB0BC", 700: "#C4D0D8", 800: "#DCE6EC", 900: "#E6EDF2",
  },
} as const;

export type ColorTokens = typeof lightTokens;

export const tokensFor = (mode: ThemeMode): ColorTokens =>
  mode === "dark" ? (darkTokens as unknown as ColorTokens) : lightTokens;

export const RADIUS = 8;
export const BASE_FONT_SIZE = 14;
export const FONT_FAMILY =
  '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
